/**
 * Email helper — calls the send-email edge function to log/send emails.
 */

const EMAIL_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;

export interface SendEmailParams {
  to: string;
  cc?: string;
  subject: string;
  body: string;
  email_type: string;
  user_id?: string;
  program_id?: string;
  license_id?: string;
  amount?: number;
  currency?: string;
  metadata?: Record<string, unknown>;
}

export async function sendEmail(params: SendEmailParams): Promise<{ success: boolean; error: string | null }> {
  try {
    const response = await fetch(EMAIL_FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error ?? `Request failed (${response.status})` };
    }

    return { success: true, error: null };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : "Unknown error" };
  }
}

export function buildRegistrationConfirmationEmail(
  name: string,
  orgName: string,
  deptName: string,
): { subject: string; body: string } {
  return {
    subject: `Welcome to ${orgName} on Sentinel`,
    body: `Hi ${name},

Your registration for the ${deptName} department at ${orgName} has been submitted.

Your account is now pending approval from your company administrator. Once approved, you'll be able to access your training portal, view assigned courses, and begin your certification journey.

If you have any questions, please contact your company administrator.

— The Sentinel Team`,
  };
}

export function buildApprovalEmail(
  name: string,
  orgName: string,
  approved: boolean,
): { subject: string; body: string } {
  if (approved) {
    return {
      subject: `Your ${orgName} account has been approved`,
      body: `Hi ${name},

Your account at ${orgName} has been approved! You can now log in to your training portal and access your assigned courses.

Visit your portal to get started: ${window.location.origin}/portal

— The Sentinel Team`,
    };
  }
  return {
    subject: `Update on your ${orgName} registration`,
    body: `Hi ${name},

We're writing to let you know that your registration for ${orgName} was not approved at this time. If you believe this is an error, please contact your company administrator.

— The Sentinel Team`,
  };
}

export function buildCourseAssignmentEmail(
  name: string,
  programTitle: string,
  dueDate: string | null,
): { subject: string; body: string } {
  const dueLine = dueDate ? `Please complete this course by ${dueDate}.` : "There is no specific due date, but please complete it at your earliest convenience.";
  return {
    subject: `New course assigned: ${programTitle}`,
    body: `Hi ${name},

A new course has been assigned to you:

  ${programTitle}

${dueLine}

Log in to your Sentinel portal to get started.

— The Sentinel Team`,
  };
}

export function buildAdminTransferEmail(
  name: string,
  orgName: string,
  transferred: boolean,
): { subject: string; body: string } {
  if (transferred) {
    return {
      subject: `You are now the Portal Admin for ${orgName}`,
      body: `Hi ${name},

You have been assigned as the portal administrator for ${orgName}.

As portal admin, you can:
- Approve or reject employee registrations
- Generate department access codes
- Assign courses to employees and departments
- Manage portal branding (logo, colors, tagline)
- Promote or transfer admin rights

Visit your portal management page to get started.

— The Sentinel Team`,
    };
  }
  return {
    subject: `You've been promoted to Portal Admin at ${orgName}`,
    body: `Hi ${name},

You've been promoted to portal administrator for ${orgName}.

As portal admin, you can:
- Approve or reject employee registrations
- Generate department access codes
- Assign courses to employees and departments
- Manage portal branding (logo, colors, tagline)
- Promote or transfer admin rights

Visit your portal management page to get started.

— The Sentinel Team`,
  };
}

export function buildInviteEmail(
  orgName: string,
  portalSlug: string,
  inviterName: string,
  accessCode: string | null,
): { subject: string; body: string } {
  const portalUrl = `${window.location.origin}/portal/${portalSlug}/register`;
  const codeLine = accessCode
    ? `\nYour department access code is: ${accessCode}\n\nEnter this code during registration to join your department.`
    : "";
  return {
    subject: `You're invited to join ${orgName} on Sentinel`,
    body: `Hi,

${inviterName} has invited you to join ${orgName} on the Sentinel certification platform.

To get started:
1. Visit ${portalUrl}
2. Create your account with your email address
3. Set your password${codeLine}

Once your account is approved by your portal administrator, you'll be able to access your training and certifications.

— The Sentinel Team`,
  };
}
